import React from 'react';
import { useQuery } from '@apollo/client/react';
import { FieldChangeGrid } from '@pages/admin/audit-log/change-grid';
import {
    getFieldChangeItems,
    getProcessedFieldChangeItems,
    removePropertiesFromObject,
} from '@pages/admin/audit-log/service';
import { GET_STUDY_DASHBOARD_WIDGETS } from '@queries/central-monitoring';
import { auditLogsForStudy_OpraQueries_auditLogsForStudy_AuditRecord } from '@queries/generated/audit.generated';
import { getAllWidgetsForStudy, getAllWidgetsForStudyVariables } from '@queries/generated/central-monitoring.generated';

interface RecordProps {
    WidgetIds?: number[];
    WidgetNames: string;
    DisplayIndex?: number;
    DisplayName?: string;
    IconName?: number;
    TableCode: number;
    TypeCode?: number;
    FieldName: string;
}

interface StudyDashboardConfigWidgetAuditLogGridProps {
    oldRecord: RecordProps;
    newRecord: RecordProps;
    oldItem?: Record<string, unknown>;
    newItem?: Record<string, unknown>;
    record: auditLogsForStudy_OpraQueries_auditLogsForStudy_AuditRecord;
}

const propArray = ['CfId', 'ConfigId', 'FieldKey'];

const StudyDashboardConfigWidgetAuditLogGrid = ({
    oldRecord,
    newRecord,
    oldItem,
    newItem,
    record,
}: StudyDashboardConfigWidgetAuditLogGridProps) => {
    const { data: widgetData } = useQuery<getAllWidgetsForStudy, getAllWidgetsForStudyVariables>(
        GET_STUDY_DASHBOARD_WIDGETS,
        {
            variables: {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                studyId: record?.studyId,
                timestamp: newItem?.updated,
            },
            fetchPolicy: 'no-cache',
        },
    );

    const refactorRecord = (rec: RecordProps) => {
        // Add empty WidgetIds array to keep arrayOfObjectsV2 happy
        if (!rec.WidgetIds) {
            rec.WidgetIds = [];
        }

        return {
            ...rec,
            ...removePropertiesFromObject(record, propArray),
        } as Record<string, unknown>;
    };

    const refactoredOldRecord = refactorRecord(oldRecord);
    const refactoredNewRecord = refactorRecord(newRecord);

    const arrayOfObjectsV2 = {
        WidgetIds: {
            valueResolver: (recordData: unknown, index: number) => {
                if (!recordData) {
                    return null;
                }

                const data = recordData as { $oid: string };
                const widget = widgetData?.getAllWidgetsForStudy?.find((el) => el?.id === data.$oid);

                if (!widget) {
                    return null;
                }

                return `Order: ${index + 1}: \n RecordId ${widget?.recordId} \nName: ${widget?.displayName}`;
            },
            compareField: '$oid',
        },
    };

    const items = getFieldChangeItems(refactoredOldRecord, refactoredNewRecord, null, (field: string) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return getProcessedFieldChangeItems({
            oldRecord: refactoredOldRecord,
            newRecord: refactoredNewRecord,
            oldItem,
            newItem,
            field,
            arrayOfObjectsV2,
        });
    });

    return <FieldChangeGrid items={items} />;
};

export default StudyDashboardConfigWidgetAuditLogGrid;
